import { onReady } from '../base/init.js';

const YANDEX_MAPS_API = 'https://api-maps.yandex.ru/2.1/?lang=ru_RU';

// Карты Яндекса ставят свои cookie: скрипт API грузим только после согласия (proposal 0025, consent-gate.twig).
function afterConsent(fn) {
  if (window.ismartConsent) window.ismartConsent.onAccept(fn);
  else fn();
}

// Ленивая загрузка Yandex Maps API: тяжёлый full.js (~700 КиБ) и его сторонние куки
// подключаются только когда карта подходит к вьюпорту (она ниже сгиба). Так Карты
// уходят из начальной загрузки страницы — без них быстрее LCP/TTI и чище аудит куки.
let mapsApiPromise = null;

// Тяжёлый рендер карты (создание ymaps.Map + отрисовка тайлов, ~100мс) уводим в idle-слот:
// если init подходит во время активного скролла, requestIdleCallback дождётся паузы и
// отрисует карту тогда — спайк не попадёт на кадр скролла, стеклянные элементы не мерцают.
// timeout 2с — страховка: карта появится даже при непрерывной активности.
function whenIdle(cb) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, 1);
  }
}

function loadMapsApi() {
  if (mapsApiPromise) return mapsApiPromise;
  mapsApiPromise = new Promise((resolve) => {
    if (window.ymaps && typeof window.ymaps.ready === 'function') {
      resolve(window.ymaps);
      return;
    }
    const existing = document.querySelector('script[data-yandex-maps-api="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.ymaps), { once: true });
      return;
    }
    afterConsent(() => {
      // Пока ждали согласия, скрипт мог поставить соседний блок карт — второй не грузим.
      if (window.ymaps && typeof window.ymaps.ready === 'function') {
        resolve(window.ymaps);
        return;
      }
      const queued = document.querySelector('script[data-yandex-maps-api="true"]');
      if (queued) {
        queued.addEventListener('load', () => resolve(window.ymaps), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = YANDEX_MAPS_API;
      script.async = true;
      script.dataset.yandexMapsApi = 'true';
      script.onload = () => resolve(window.ymaps);
      document.head.appendChild(script);
    });
  });
  return mapsApiPromise;
}

function initMap(el) {
  const containerId = el.dataset.containerId;
  const address = el.dataset.address;
  // data-points="lat,lon;lat,lon" — несколько ДЦ на одной карте
  const points = (el.dataset.points || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.split(',').map(Number));
  const center = el.dataset.center ? el.dataset.center.split(',').map(Number) : null;
  const baseZoom = parseInt(el.dataset.zoom, 10) || 16;
  const zoom = window.innerWidth >= 992 ? baseZoom + 3 : baseZoom;
  const icon = el.dataset.icon;
  const imageSize = (el.dataset.imageSize || '64,90').split(',').map(Number);
  const imageOffset = (el.dataset.imageOffset || '-32,-90').split(',').map(Number);

  window.ymaps.ready(() => {
    whenIdle(() => {
      const map = new window.ymaps.Map(containerId, {
        center: center || [55.751244, 37.618423],
        zoom,
        controls: ['zoomControl'],
      });
      map.behaviors.disable('scrollZoom');
      if (window.innerWidth < 768) map.behaviors.disable('drag');

      const addMarker = (coords, recenter = true) => {
        const placemark = new window.ymaps.Placemark(
          coords,
          {},
          {
            iconLayout: 'default#image',
            iconImageHref: icon,
            iconImageSize: imageSize,
            iconImageOffset: imageOffset,
            hasBalloon: false, // балун скрыт — только метка
          }
        );
        map.geoObjects.add(placemark);
        if (recenter) map.setCenter(coords, zoom);
      };

      if (points.length > 1) {
        points.forEach((p) => addMarker(p, false));
        map.setBounds(map.geoObjects.getBounds(), { checkZoomRange: true, zoomMargin: 48 });
      } else if (points.length === 1) {
        addMarker(points[0]);
      } else if (center) {
        addMarker(center);
      } else if (address) {
        window.ymaps
          .geocode(address, { results: 1 })
          .then((res) => {
            const obj = res.geoObjects.get(0);
            if (obj) addMarker(obj.geometry.getCoordinates());
          })
          .catch(() => {});
      }
      el.classList.add('is-loaded');
    });
  });
}

function activate(el) {
  if (el.dataset.inited) return;
  el.dataset.inited = '1';
  loadMapsApi().then(() => initMap(el));
}

function boot() {
  const maps = document.querySelectorAll('.js-map');
  if (!maps.length) return;

  if (typeof IntersectionObserver === 'undefined') {
    maps.forEach(activate);
    return;
  }

  // Наблюдаем стабильную секцию-родитель, а не сам .js-map: его высота появляется
  // только после рендера карты (canvas height:100%), поэтому 0-высотный таргет не даёт
  // IntersectionObserver сработать (пустой intersection-rect) — дедлок. У секции высота есть.
  const io = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          activate(entry.target.__mapEl);
        }
      });
    },
    { rootMargin: '400px' }
  );
  maps.forEach((el) => {
    const target = el.closest('section') || el.parentElement || el;
    target.__mapEl = el;
    io.observe(target);
  });
}

onReady(boot);
